import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { apiDelete, apiGet, apiPost, apiPut } from '@/api/client';
import type { CompanyDomain, CompanySettings } from '@/api/types';
import { AsyncSection } from '@/app/components/AsyncSection';
import { SubmitError } from '@/app/components/SubmitError';
import { Alert } from '@/app/components/ui/Alert';
import { Button } from '@/app/components/ui/Button';
import { Card } from '@/app/components/ui/Card';
import { EmptyState } from '@/app/components/ui/EmptyState';
import { Field, FormActions, Input } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';

/** Admin only: POST /companies, then straight to the new company's page. */
export function CompanyNewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const form = useSubmit(async () => {
    const created = await apiPost('/companies', { body: { name } });
    await navigate(`/manage/companies/${created?.company_id ?? ''}`);
  });

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('companies.form.newTitle')} />
      <Card className="max-w-2xl">
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void form.submit();
          }}
        >
          <Field label={t('companies.form.name')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </Field>
          <SubmitError error={form.error} />
          <FormActions>
            <Button as={Link} to="/manage/companies" variant="ghost">
              {t('companies.form.cancel')}
            </Button>
            <Button type="submit" disabled={form.pending || name.trim() === ''}>
              {t('companies.form.create')}
            </Button>
          </FormActions>
        </form>
      </Card>
    </section>
  );
}

/** Admin or this company's admin: PUT /companies/{id} (name only). */
export function CompanyEditPage() {
  const { t } = useTranslation();
  const { companyId = '' } = useParams();
  const navigate = useNavigate();
  const company = useAsync(
    () => apiGet('/companies/{companyId}', { params: { companyId } }),
    [companyId],
  );
  const [name, setName] = useState<string | null>(null);
  const form = useSubmit(async (value: string) => {
    await apiPut('/companies/{companyId}', { params: { companyId }, body: { name: value } });
    await navigate(`/manage/companies/${companyId}`);
  });

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('companies.form.editTitle')} />
      <AsyncSection state={company} label={t('companies.detail.thisCompany')}>
        {(data) => {
          const current = name ?? data.name ?? '';
          return (
            <Card className="max-w-2xl">
              <form
                className="flex flex-col gap-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  void form.submit(current);
                }}
              >
                <Field label={t('companies.form.name')}>
                  <Input value={current} onChange={(e) => setName(e.target.value)} required />
                </Field>
                <SubmitError error={form.error} />
                <FormActions>
                  <Button as={Link} to={`/manage/companies/${companyId}`} variant="ghost">
                    {t('companies.form.cancel')}
                  </Button>
                  <Button type="submit" disabled={form.pending || current.trim() === ''}>
                    {t('companies.form.save')}
                  </Button>
                </FormActions>
              </form>
            </Card>
          );
        }}
      </AsyncSection>
    </section>
  );
}

/** Admin only, and destructive: DELETE /companies/{id} behind a typed confirmation. */
export function CompanyDeletePage() {
  const { t } = useTranslation();
  const { companyId = '' } = useParams();
  const navigate = useNavigate();
  const company = useAsync(
    () => apiGet('/companies/{companyId}', { params: { companyId } }),
    [companyId],
  );
  const [confirmation, setConfirmation] = useState('');
  const form = useSubmit(async () => {
    await apiDelete('/companies/{companyId}', { params: { companyId } });
    await navigate('/manage/companies');
  });

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('companies.form.deleteTitle')} />
      <AsyncSection state={company} label={t('companies.detail.thisCompany')}>
        {(data) => (
          <Card className="max-w-2xl">
            <form
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                void form.submit();
              }}
            >
              <Alert tone="warning">
                {t('companies.form.deleteLead', { name: data.name ?? '' })}
              </Alert>
              <Field label={t('companies.form.companyNameLabel')}>
                <Input
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  placeholder={data.name ?? ''}
                />
              </Field>
              <SubmitError error={form.error} />
              <FormActions>
                <Button as={Link} to={`/manage/companies/${companyId}`} variant="ghost">
                  {t('companies.form.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="danger"
                  disabled={form.pending || confirmation !== data.name}
                >
                  {t('companies.form.deletePermanently')}
                </Button>
              </FormActions>
            </form>
          </Card>
        )}
      </AsyncSection>
    </section>
  );
}

/** Admin only: PUT /companies/{id}/settings — min_group_size, null for default. */
export function CompanySettingsPage() {
  const { t } = useTranslation();
  const { companyId = '' } = useParams();
  const navigate = useNavigate();
  const settings = useAsync(
    () =>
      apiGet('/companies/{companyId}/settings', {
        params: { companyId },
      }) as Promise<CompanySettings>,
    [companyId],
  );
  const [value, setValue] = useState<string | null>(null);
  const form = useSubmit(async (raw: string) => {
    await apiPut('/companies/{companyId}/settings', {
      params: { companyId },
      body: { min_group_size: raw === '' ? null : Number(raw) },
    });
    await navigate(`/manage/companies/${companyId}`);
  });

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader
        title={t('companies.form.settingsTitle')}
        lead={t('companies.form.settingsLead')}
      />
      <AsyncSection state={settings} label={t('companies.detail.settingsLabel')}>
        {(data) => {
          const current = value ?? String(data.min_group_size ?? '');
          return (
            <Card className="max-w-2xl">
              <form
                className="flex flex-col gap-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  void form.submit(current);
                }}
              >
                <Field label={t('companies.form.minGroupSize')}>
                  <Input
                    type="number"
                    min={3}
                    value={current}
                    onChange={(e) => setValue(e.target.value)}
                  />
                </Field>
                <SubmitError error={form.error} />
                <FormActions>
                  <Button as={Link} to={`/manage/companies/${companyId}`} variant="ghost">
                    {t('companies.form.cancel')}
                  </Button>
                  <Button type="submit" disabled={form.pending}>
                    {t('companies.form.save')}
                  </Button>
                </FormActions>
              </form>
            </Card>
          );
        }}
      </AsyncSection>
    </section>
  );
}

/** Admin only: manage a company's e-mail domains (add and remove). */
export function CompanyDomainsPage() {
  const { t } = useTranslation();
  const { companyId = '' } = useParams();
  const domains = useAsync(
    () =>
      apiGet('/companies/{companyId}/domains', {
        params: { companyId },
      }) as unknown as Promise<CompanyDomain[]>,
    [companyId],
  );
  const [domain, setDomain] = useState('');
  const [warning, setWarning] = useState<string | null>(null);
  const add = useSubmit(async () => {
    const result = (await apiPost('/companies/{companyId}/domains', {
      params: { companyId },
      body: { domain },
    })) as { warning?: string } | undefined;
    setWarning(result?.warning ?? null);
    setDomain('');
    domains.reload();
  });
  const remove = useSubmit(async (value: string) => {
    await apiDelete('/companies/{companyId}/domains', {
      params: { companyId },
      body: { domain: value },
    });
    domains.reload();
  });

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('companies.form.domainsTitle')} lead={t('companies.form.domainsLead')} />

      <Card className="max-w-2xl">
        <AsyncSection state={domains} label={t('companies.detail.domainsLabel')}>
          {(rows) =>
            rows.length === 0 ? (
              <EmptyState text={t('companies.detail.domainsEmpty')} />
            ) : (
              <ul className="flex flex-col gap-2">
                {rows.map((row) => (
                  <li key={row.id} className="text-ink flex items-center justify-between text-sm">
                    {row.domain}
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      disabled={remove.pending}
                      onClick={() => void remove.submit(row.domain)}
                    >
                      {t('companies.form.remove')}
                    </Button>
                  </li>
                ))}
              </ul>
            )
          }
        </AsyncSection>
        <SubmitError error={remove.error} />
      </Card>

      <Card className="max-w-2xl">
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void add.submit();
          }}
        >
          <Field label={t('companies.form.addDomain')}>
            <Input
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="widget-corp.nl"
              required
            />
          </Field>
          <SubmitError error={add.error} />
          {warning && (
            <Alert tone="warning" role="status">
              {warning}
            </Alert>
          )}
          <FormActions>
            <Button as={Link} to={`/manage/companies/${companyId}`} variant="ghost">
              {t('companies.form.backToCompany')}
            </Button>
            <Button type="submit" disabled={add.pending || domain.trim() === ''}>
              {t('companies.form.add')}
            </Button>
          </FormActions>
        </form>
      </Card>
    </section>
  );
}
